import { useEffect, useState, useRef } from 'react';
import './ShuffleText.css';

const ShuffleText = ({
  text,
  className = '',
  duration = 2000,
  shuffleSpeed = 50,
  startDelay = 0,
  onComplete
}) => {
  const characters = '0123456789!@#$%^&*';
  const thetaSymbol = 'θ'; // Theta for special "o" animation

  const [displayText, setDisplayText] = useState(text);
  const [isAnimating, setIsAnimating] = useState(false);
  const mountedRef = useRef(true);
  const shuffleDurationPerChar = 500; // 0.5 second per character
  const numShuffles = 8; // Show 8 random characters
  const oFlickerDuration = 400; // Just one jerk: o -> θ -> o (200ms each)
  const oFlickerSpeed = 200; // 200ms per flicker (slow jerky effect)

  // Generate Fibonacci positions up to text length
  const getFibonacciPositions = () => {
    const positions = [0, 1];
    while (true) {
      const next = positions[positions.length - 1] + positions[positions.length - 2];
      if (next >= text.length) break;
      positions.push(next);
    }
    return positions;
  };
  const fibPositions = getFibonacciPositions();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const startAnimation = () => {
      if (!mountedRef.current) return;

      setIsAnimating(true);
      let iteration = 0;
      const shuffleInterval = shuffleDurationPerChar / numShuffles; // 500ms / 8 = 62.5ms per character
      const maxIterations = duration / shuffleInterval;

      const animate = () => {
        if (!mountedRef.current) return;

        if (iteration < maxIterations) {
          let newText = '';

          // Calculate elapsed time
          const elapsedTime = iteration * shuffleInterval;
          const lastFibPosition = fibPositions[fibPositions.length - 1]; // Position 55
          const lastFibDelay = 2000; // Last Fibonacci position starts after 2s
          const lastFibEnd = lastFibDelay + shuffleDurationPerChar;

          // Special "o" in "Automation" - find the second "o"
          const automationIndex = text.indexOf('Automation');
          const oPosition = automationIndex + 8; // "Automati[o]n" - the 9th character (index 8)
          const oDelay = 3000; // 2.5s (u finishes) + 0.5s delay = 3s
          const oEnd = oDelay + oFlickerDuration; // 1 second flicker

          // Fibonacci positions: first 4 staggered, rest immediate, last separate, plus special "o"
          for (let i = 0; i < text.length; i++) {
            const isSpace = text[i] === ' ';
            const isLastFib = (i === lastFibPosition);
            const isSpecialO = (i === oPosition);
            const fibIndex = fibPositions.indexOf(i);

            if (isSpace) {
              // Never animate spaces
              newText += ' ';
            } else if (isSpecialO && elapsedTime >= oDelay && elapsedTime < oEnd) {
              // Special "o" alternates between "o" and "θ" slowly (every 200ms)
              const flickerTime = Math.floor((elapsedTime - oDelay) / oFlickerSpeed);
              const showTheta = flickerTime % 2 === 1; // Odd = theta, even = "o"
              newText += showTheta ? thetaSymbol : 'o';
            } else if (isLastFib && elapsedTime >= lastFibDelay && elapsedTime < lastFibEnd) {
              // Last Fibonacci position (55) shuffles from 2s to 2.5s
              const randomChar = characters[Math.floor(Math.random() * characters.length)];
              newText += randomChar;
            } else if (!isLastFib && fibIndex !== -1) {
              // First 4 positions (0,1,2,3) get staggered delays
              // Positions 5+ (5,8,13,21,34) start immediately at 0s
              const charStartDelay = fibIndex < 4 ? fibIndex * 500 : 0;
              const charEndDelay = charStartDelay + shuffleDurationPerChar;

              if (elapsedTime >= charStartDelay && elapsedTime < charEndDelay) {
                const randomChar = characters[Math.floor(Math.random() * characters.length)];
                newText += randomChar;
              } else {
                newText += text[i];
              }
            } else {
              // Keep correct character
              newText += text[i];
            }
          }

          setDisplayText(newText);
          iteration++;

          setTimeout(animate, shuffleInterval);
        } else {
          // Animation complete - show final text
          setDisplayText(text);
          setIsAnimating(false);
          if (onComplete) onComplete();
        }
      };

      animate();
    };

    const timeout = setTimeout(startAnimation, startDelay);

    return () => {
      clearTimeout(timeout);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, duration, shuffleSpeed, startDelay, onComplete, characters]);

  return (
    <span className={`shuffle-text ${isAnimating ? 'shuffling' : ''} ${className}`}>
      {displayText || text}
    </span>
  );
};

export default ShuffleText;
